/**
 * Discover jobs API (discover-api orchestration). Uses discoverRuns / PRIV_PLAT_API.
 */
import { API } from '../Auth';
import { getDataSourceNumericId } from '../DataSources/datasourcesApi';
import { parsePresetTemplateId, listSamplingRuleTemplates } from './samplingRulesApi';
import { parentFolderOfPath } from '../Utils/fileTypeUtils';

const EP = 'discoverRuns';
const JOBS = '/api/v1/discover/jobs';

// Bounded LRU of parsed worker overview payloads. Each entry is a full 5–7 MB parsed
// JsonNode-equivalent at facets scale, so an unbounded map pinned one payload per job the
// user (or the Masking/Encryption source pages) ever touched — never evicted (audit §3 UI).
// Keep only the most-recently-used MAX_RESULTS_CACHE jobs. Map iteration is insertion-order,
// so we delete-then-set on every read/write to move the touched key to the newest slot.
const MAX_RESULTS_CACHE = 2;
const resultsCache = new Map();

function cacheKey(jobId) {
  return String(jobId);
}

function cacheGet(key) {
  if (!resultsCache.has(key)) return undefined;
  const entry = resultsCache.get(key);
  // Touch: re-insert so this key becomes most-recently-used.
  resultsCache.delete(key);
  resultsCache.set(key, entry);
  return entry;
}

function cacheSet(key, entry) {
  resultsCache.delete(key);
  resultsCache.set(key, entry);
  while (resultsCache.size > MAX_RESULTS_CACHE) {
    // Oldest key is the first in insertion order.
    const oldest = resultsCache.keys().next().value;
    resultsCache.delete(oldest);
  }
}

export function clearDiscoverResultsCache(jobId) {
  if (jobId != null) resultsCache.delete(cacheKey(jobId));
  else resultsCache.clear();
}

/**
 * Extract tables array from worker results payload.
 * @param {object} payload
 * @returns {Array}
 */
export function extractTablesFromPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.tables)) return payload.tables;
  if (Array.isArray(payload)) return payload;
  return [];
}

/**
 * Row key for an overview table item: `schema.table` when the item carries a schema
 * (multi-schema JDBC; NoSQL database name), bare table name otherwise (file sources and
 * owner-less runs — the worker omits `schema` when empty). The single source of the
 * inventory/scatter keyspace: keying by bare name silently collapsed same-named tables
 * across schemas (e.g. tenant_a/tenant_b/tenant_c each owning `patients`) into one row.
 */
export function overviewTableKey(table) {
  const name = table && table.name;
  if (!name) return null;
  return table.schema ? `${table.schema}.${name}` : name;
}

/**
 * Folder-index entries from a stored results payload. Folder Search
 * (FILE_GENERIC_FOLDER) runs persist `{run_id, folders, scan_meta}` — the worker's
 * folder index — instead of the monolithic tables payload (which measured 5-7 MB at
 * facets scale and was rewritten every poll). Null for classic tables payloads, so
 * callers can branch: `folders ? folder-grouped view : classic view`.
 */
export function extractFoldersFromPayload(payload) {
  if (!payload) return null;
  return Array.isArray(payload.folders) ? payload.folders : null;
}

/**
 * Re-exported from Utils/fileTypeUtils, which is its single implementation. It moved there so
 * pure consumers can derive folders without importing this API client (and so suites that
 * jest.mock this module don't lose the helper). Import from either path.
 */
export { parentFolderOfPath };

/**
 * GET /api/v1/discover/jobs/{jobId}/results/folders — live pass-through of the worker's
 * folder index: `{run_id, folders: [{folder_path, file_count, finding_count,
 * entity_rollup}], scan_meta}`. Small however large the run.
 */
export const getFolderIndex = async (jobId) => {
  return API.get(EP, `${JOBS}/${encodeURIComponent(jobId)}/results/folders`);
};

/**
 * GET /api/v1/discover/jobs/{jobId}/results/folder?path= — one folder's slice in the
 * exact overview table-item shape. Direct-children-only; chunks tile the run. Pass ""
 * (or omit) for files directly under the scan root.
 */
export const getFolderChunk = async (jobId, folderPath) => {
  return API.get(EP, `${JOBS}/${encodeURIComponent(jobId)}/results/folder`, {
    queryStringParameters: { path: folderPath ?? '' },
  });
};

/**
 * GET /api/v1/discover/jobs/{jobId}/fanout — "Discover everything" ledger for a Folder
 * Search parent job: one row per candidate `{folder_path, datasource_type, state,
 * child_data_source_id, child_job_id, error}`. Empty list when the run did not opt in.
 */
export const getDiscoverFanout = async (jobId) => {
  return API.get(EP, `${JOBS}/${encodeURIComponent(jobId)}/fanout`);
};

/**
 * POST /api/v1/discover/jobs/{jobId}/fanout/cancel — cancel the remaining fan-out:
 * queued rows flip CANCELLED, still-running child jobs get a best-effort cancel.
 * Returns the refreshed ledger.
 */
export const cancelDiscoverFanout = async (jobId) => {
  return API.post(EP, `${JOBS}/${encodeURIComponent(jobId)}/fanout/cancel`);
};

/**
 * Entity totals summed across a folder index's rollups: `{ENTITY: count}`. Feeds the
 * overview donut for Folder Search runs. NOTE the semantics differ from the classic
 * field-count donut — rollups are sampled-instance counts, the only aggregate the
 * index carries — but the relative distribution is what the donut communicates.
 */
export const buildOverviewFromFolderIndex = (folders = []) => {
  const totals = {};
  (folders || []).forEach((f) => {
    Object.entries(f.entity_rollup || {}).forEach(([entity, count]) => {
      totals[entity] = (totals[entity] || 0) + (Number(count) || 0);
    });
  });
  return totals;
};

/**
 * Table-index results mode (PeopleSoft-80K readiness). Very large DB runs (tens of
 * thousands of tables) persist a small always-loaded INDEX — `{run_id,
 * results_mode:"table_index", total_tables, sensitive_table_count, total_fields,
 * entity_totals, schemas, scan_meta}` — instead of the classic monolithic `tables`
 * array. Mirrors extractFoldersFromPayload: null for classic/folder payloads, so
 * callers branch `tableIndex ? index view : classic/folder view`.
 */
export function extractIndexFromPayload(payload) {
  if (!payload) return null;
  return payload.results_mode === 'table_index' ? payload : null;
}

/**
 * Entity totals straight off a table index's `entity_totals: {TYPE: count}` — already
 * the aggregate the index carries, so this just guards shape/type (mirrors
 * buildOverviewFromFolderIndex's role for the folder index).
 */
export const buildOverviewFromIndex = (index) => {
  const totals = (index && index.entity_totals) || {};
  const result = {};
  Object.entries(totals).forEach(([entity, count]) => {
    result[entity] = Number(count) || 0;
  });
  return result;
};

/**
 * GET /api/v1/discover/jobs/{jobId}/results/index — live pass-through of the worker's
 * table index (see extractIndexFromPayload). Small however large the run; the classic
 * results endpoint returns the same shape for index-mode runs (the stored payload IS
 * the index), so callers normally get this for free via getOverview/getMeta. Exposed
 * directly for callers that want a guaranteed-fresh index without touching the results
 * cache.
 */
export const getResultsIndex = async (jobId) => {
  return API.get(EP, `${JOBS}/${encodeURIComponent(jobId)}/results/index`);
};

/**
 * GET /api/v1/discover/jobs/{jobId}/results/findings-summary — the worker's sparse
 * findings view: ONLY tables/fields with at least one finding, in the classic
 * `tables[].fields[].findings` shape. MB-sized even on table-index (PeopleSoft-80K)
 * runs, so it's the right bulk read for summary/aggregate consumers (risk profile,
 * time series) that would otherwise page the full table set. Not a substitute for the
 * overview: totals for ALL tables/fields must come from the index.
 */
export const getResultsFindingsSummary = async (jobId) => {
  return API.get(EP, `${JOBS}/${encodeURIComponent(jobId)}/results/findings-summary`);
};

/**
 * GET /api/v1/discover/jobs/{jobId}/results/tables — paged table rows for index-mode
 * runs. `detail=summary` (default) is cheap; `detail=full` items are the exact
 * OverviewTableItem shape the classic `tables` array carries (fields + findings), and
 * are capped at 200/page by the server (summary caps at 500).
 * @param {string|number} jobId
 * @param {{page?: number, size?: number, sort?: 'name'|'findings', dir?: 'asc'|'desc',
 *   schema?: string, q?: string, entity?: string, detail?: 'summary'|'full'}} [params]
 */
export const getResultsTablesPage = async (jobId, params = {}) => {
  return API.get(EP, `${JOBS}/${encodeURIComponent(jobId)}/results/tables`, {
    queryStringParameters: params,
  });
};

/**
 * GET /api/v1/discover/jobs/{jobId}/results/table?name=&schema= — one table's full
 * OverviewTableItem. Index mode's equivalent of `overview.tables.find(...)` on a
 * classic payload — the one place the UI needs a single table's fields without
 * paging through the whole index.
 */
export const getTableChunk = async (jobId, name, schema) => {
  const params = { name };
  if (schema != null && schema !== '') params.schema = schema;
  return API.get(EP, `${JOBS}/${encodeURIComponent(jobId)}/results/table`, {
    queryStringParameters: params,
  });
};

function mapJobStatusToUi(status) {
  if (status === 'PENDING') return 'QUEUED';
  if (status === 'SUCCEEDED') return 'COMPLETED';
  return status;
}

/**
 * Canonical job id for routes and GET /jobs/{id}. API returns numeric `id`; `externalJobId` is the worker UUID, not the path id.
 */
export function jobIdFromJob(job) {
  if (!job) return undefined;
  const raw = job.id != null ? job.id : job.jobId;
  if (raw == null) return undefined;
  return String(raw);
}

/**
 * Progress for UI: use a consistent numerator/denominator pair — never mix work-item counts with record totals.
 * Prefer record progress when available; otherwise work-item (e.g. tables) progress.
 */
function progressFieldsFromJob(job, cached) {
  const recordsProcessed =
    job.recordsProcessed ??
    cached.recordsProcessed ??
    cached.rows_processed ??
    cached.rowsProcessed ??
    job.recordsCompleted ??
    cached.recordsCompleted;
  const recordsTotal =
    job.recordsTotal ??
    cached.recordsTotal ??
    cached.total_rows_to_process ??
    cached.totalRowsToProcess ??
    cached.total_rows;

  const workCompleted = job.workItemsCompleted ?? cached.workItemsCompleted;
  const workTotal = job.workItemsTotal ?? cached.workItemsTotal;

  if (recordsTotal > 0 && recordsProcessed != null) {
    return { rows_processed: recordsProcessed, total_rows_to_process: recordsTotal };
  }
  if (workTotal > 0 && workCompleted != null) {
    return { rows_processed: workCompleted, total_rows_to_process: workTotal };
  }

  return { rows_processed: undefined, total_rows_to_process: undefined };
}

function mapOrchestrationJobToRun(job) {
  if (!job || job.jobType !== 'DISCOVER') return null;
  const idStr = jobIdFromJob(job);
  if (!idStr) return null;
  const cached = parseCachedStatusJson(job.cachedStatusJson);
  const { rows_processed, total_rows_to_process } = progressFieldsFromJob(job, cached);
  const started = job.startedAt ? Date.parse(job.startedAt) : null;
  const finished = job.finishedAt ? Date.parse(job.finishedAt) : null;
  // Worker may surface a non-error advisory (e.g. "scanning all schemas"). Prefer the value the
  // platform-api lifted onto the DTO, but fall back to the cached worker payload for older builds.
  const notice = job.notice ?? cached.notice ?? null;
  return {
    run_id: idStr,
    jobId: idStr,
    status: mapJobStatusToUi(job.status) || job.status,
    connection: job.dataSourceName ?? '',
    engine: job.engine ?? '',
    label: job.runLabel ?? idStr,
    sampling_rules: job.samplingRuleName ?? '-',
    start_date: started,
    completed_at: finished,
    rows_processed,
    total_rows_to_process,
    workItemKind: job.workItemKind ?? null,
    workItemsCompleted: job.workItemsCompleted ?? null,
    workItemsTotal: job.workItemsTotal ?? null,
    error_message: job.errorCode || job.upstreamErrorBody || undefined,
    upstream_error: stringFromUpstreamBody(job.upstreamErrorBody),
    notice: notice || undefined,
    _rawStatus: job.status,
    dataSourceId: job.dataSourceId ?? null,
  };
}

function parseCachedStatusJson(raw) {
  if (raw == null || raw === '') return {};
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return {};
  }
}

/** Prefer for UI tooltips: raw upstream body from the worker/service (string or JSON). */
function stringFromUpstreamBody(body) {
  if (body == null || body === '') return undefined;
  if (typeof body === 'string') return body;
  if (typeof body === 'object') {
    try {
      return JSON.stringify(body);
    } catch {
      return String(body);
    }
  }
  return String(body);
}

/**
 * GET /api/v1/discover/jobs — Spring Page; filter DISCOVER only.
 * @param {number} [page=0]
 * @param {number} [size=20]
 * @param {string} [sort='startedAt,desc']
 * @param {number} [dataSourceId]
 * @returns {Promise<{ total_count, page, size, total_pages, runs }>}
 */
export const getHistory = async (page = 0, size = 20, sort = 'startedAt,desc', dataSourceId) => {
  const params = { page, size, sort };
  if (dataSourceId != null) params.dataSourceId = dataSourceId;
  const pageDto = await API.get(EP, JOBS, { queryStringParameters: params });
  const content = (pageDto && pageDto.content) || [];
  const discoverJobs = content.filter((j) => j && j.jobType === 'DISCOVER');
  const runs = discoverJobs.map(mapOrchestrationJobToRun).filter(Boolean);
  const total = pageDto && pageDto.totalElements != null ? Number(pageDto.totalElements) : runs.length;
  const totalPages = pageDto && pageDto.totalPages != null ? pageDto.totalPages : Math.ceil(total / size) || 1;
  return {
    total_count: total,
    page: pageDto?.number ?? page,
    size: pageDto?.size ?? size,
    total_pages: totalPages,
    runs,
  };
};

/**
 * Build JobStartRequestDTO from UI connection, rules preset, and run label.
 * @param {object} connection
 * @param {object} rules
 * @param {string} label
 */
export const buildDiscoveryRequest = (connection, rules, label) => {
  const dataSourceId = getDataSourceNumericId(connection);
  if (dataSourceId == null) {
    throw new Error('Save this data source before running discovery.');
  }

  const body = { dataSourceId };
  const templateId = rules?.samplingRuleTemplateId ?? parsePresetTemplateId(rules?.id);
  if (templateId != null) {
    body.samplingRuleTemplateId = templateId;
  }

  const type = connection?.type || 'mysql';
  let samplePercent = 10;
  if (rules?.sample?.row?.size > 0 && rules?.sample?.row?.every > 0) {
    const raw = (rules.sample.row.size / rules.sample.row.every) * 100;
    // Floor at 0.001% — the lowest sample rate the discovery engine accepts. The old 0.1% floor
    // (plus 1-decimal rounding) blocked the .01/.001 percent rates the base system supports; the
    // per-table min-rows rule already prevents undersampling, so no higher UI floor is needed.
    // Round to 3 decimals so those small rates survive instead of collapsing to 0.
    samplePercent = Math.min(100, Math.max(0.001, Math.round(raw * 1000) / 1000));
  }

  const config = {};
  if (label) config.label = label;
  if (rules?.maxTables != null) config.maxTables = rules.maxTables;
  if (rules?.maxRowsPerTable != null) config.maxRowsPerTable = rules.maxRowsPerTable;

  const samplingRules = { samplePercent };
  if (rules?.maxTables != null) samplingRules.maxTables = rules.maxTables;
  if (rules?.maxRowsPerTable != null) samplingRules.maxRowsPerTable = rules.maxRowsPerTable;

  if (body.samplingRuleTemplateId == null) {
    Object.assign(config, samplingRules);
  } else {
    if (rules?.maxTables != null || rules?.maxRowsPerTable != null || samplePercent !== 10) {
      Object.assign(config, samplingRules);
    }
  }

  if (Object.keys(config).length > 0) {
    body.config = config;
  }

  return body;
};

/**
 * POST /api/v1/discover/jobs
 * @param {object} body - JobStartRequestDTO
 */
export const startDiscovery = async (body) => {
  return API.post(EP, JOBS, { body });
};

/**
 * Start a discover run when the caller has no explicit rule selection (Re-run buttons,
 * pickers, pipeline cards). The jobs endpoint rejects a bare { dataSourceId } — every
 * discover run must carry sampling config — so reuse the given sampling-rule template
 * (normally the previous run's), falling back to the default template, then to a plain
 * samplePercent when no templates exist. A stale template id (deleted preset) also
 * falls back instead of failing the start.
 */
export const startDiscoveryWithRule = async (dataSourceId, samplingRuleTemplateId = null) => {
  if (samplingRuleTemplateId != null) {
    try {
      return await startDiscovery({ dataSourceId, samplingRuleTemplateId });
    } catch (e) {
      if (e?.body?.code !== 'SAMPLING_RULE_NOT_FOUND') throw e;
    }
  }
  let templates = [];
  try {
    templates = await listSamplingRuleTemplates();
  } catch (e) {
    console.log('Failed to list sampling rules, starting with default samplePercent', e);
  }
  const def = templates.find((t) => t.builtIn === true) || templates[0] || null;
  if (def && def.id != null) {
    return startDiscovery({ dataSourceId, samplingRuleTemplateId: def.id });
  }
  return startDiscovery({ dataSourceId, config: { samplePercent: 10 } });
};

export const getJob = async (jobId) => {
  return API.get(EP, `${JOBS}/${encodeURIComponent(jobId)}`);
};

/**
 * GET /api/v1/discover/jobs/{jobId}/results
 */
export const getJobResults = async (jobId) => {
  return API.get(EP, `${JOBS}/${encodeURIComponent(jobId)}/results`);
};

/**
 * @param {string|number} jobId
 * @returns {Promise<{ jobId: string, run_id: string, status: string, rows_processed?: number, total_rows_to_process?: number, error_message?: string }>}
 */
export const getJobStatus = async (jobId) => {
  const job = await getJob(jobId);
  const cached = parseCachedStatusJson(job.cachedStatusJson);
  const { rows_processed, total_rows_to_process } = progressFieldsFromJob(job, cached);
  const id = jobIdFromJob(job) ?? String(jobId);
  const notice = job.notice ?? cached.notice ?? null;
  return {
    jobId: id,
    run_id: id,
    status: mapJobStatusToUi(job.status) || job.status,
    rows_processed,
    total_rows_to_process,
    workItemKind: job.workItemKind ?? null,
    workItemsCompleted: job.workItemsCompleted ?? null,
    workItemsTotal: job.workItemsTotal ?? null,
    error_message: job.errorCode || job.upstreamErrorBody,
    upstream_error: stringFromUpstreamBody(job.upstreamErrorBody),
    notice: notice || undefined,
  };
};

export const enrichRunsWithJobStatus = async (runs = []) => {
  const inProgress = runs.filter((r) => r.status === 'RUNNING' || r.status === 'QUEUED');
  if (inProgress.length === 0) return runs;

  const results = await Promise.all(
    inProgress.map(async (run) => {
      try {
        const key = run.run_id ?? run.jobId;
        if (key == null) return run;
        const job = await getJobStatus(key);
        const mergedId = job.run_id ?? job.jobId ?? run.run_id ?? run.jobId;
        return {
          ...run,
          run_id: mergedId,
          jobId: mergedId,
          rows_processed: job?.rows_processed ?? run.rows_processed,
          total_rows_to_process: job?.total_rows_to_process ?? run.total_rows_to_process,
          status: job?.status ?? run.status,
          ...(job?.workItemKind != null && { workItemKind: job.workItemKind }),
          ...(job?.workItemsCompleted != null && { workItemsCompleted: job.workItemsCompleted }),
          ...(job?.workItemsTotal != null && { workItemsTotal: job.workItemsTotal }),
          ...(job?.error_message != null && { error_message: job.error_message }),
          ...(job?.upstream_error != null && { upstream_error: job.upstream_error }),
          ...(job?.notice != null && { notice: job.notice }),
        };
      } catch (e) {
        return run;
      }
    })
  );

  const byId = new Map(results.map((r) => [r.run_id ?? r.jobId, r]));
  return runs.map((r) => byId.get(r.run_id ?? r.jobId) || r);
};

// Raw platform statuses (pre-mapJobStatusToUi) where the run is over and the stored
// results payload is final. FAILED/CANCELLED runs keep their last partial snapshot on
// the platform (finalizeFailed never clears results_json), so pulling it here is what
// lets a failed run render the findings it collected before dying.
// COMPLETED_WITH_ERRORS is not emitted yet (Package 3/4) — included so it lights up
// when the platform starts sending it. INTERRUPTED = worker restarted mid-run; terminal
// with failure semantics, partial snapshot preserved like FAILED.
const TERMINAL_JOB_STATUSES = new Set(['SUCCEEDED', 'FAILED', 'CANCELLED', 'COMPLETED_WITH_ERRORS', 'INTERRUPTED']);

/**
 * @param {string|number} jobId
 * @param {{ withResults?: boolean }} [opts] Pass `withResults: true` to pull the stored
 *   results payload even while the run is still in progress (slow partial-results poll).
 */
export const getMeta = async (jobId, opts = {}) => {
  const job = await getJob(jobId);
  let payload = {};
  // Only pull the full results payload once the run is terminal (any terminal status —
  // the platform serves the last partial snapshot for FAILED/CANCELLED), or when the
  // caller explicitly opts in mid-run. Fetching results on every poll while RUNNING made
  // each 15s status poll re-download + re-parse the whole overview (5–7 MB at facets
  // scale on the same worker overview N+1 that starves the sampler pool — audit R6).
  // Between partial pulls the header stats fall back to 0/empty; SearchProgress drives
  // the in-flight UI from the cheap status poll instead.
  if (TERMINAL_JOB_STATUSES.has(job.status) || opts.withResults) {
    try {
      const res = await getJobResults(jobId);
      payload = res?.payload ?? {};
      // Warm the shared results cache so the panel getOverview calls that follow this
      // pull (mount, partialTick refetch, terminal transition) are cache hits instead
      // of a second full-payload transfer.
      cacheSet(cacheKey(jobId), { payload, at: Date.now() });
    } catch (e) {
      console.log('getMeta results', e);
    }
  }
  const pTables = extractTablesFromPayload(payload);
  const tables = pTables.map((t) => ({ name: t.name }));
  let tableCount = pTables.length;
  let sensitiveCount = pTables.filter((t) =>
    (t.fields || []).some((f) => f.findings && Object.keys(f.findings).length > 0)
  ).length;

  // Folder Search runs store the folder index instead of per-file rows. Derive the
  // header counts from it: total files across folders, and folders-with-findings as
  // the sensitive signal (per-file sensitivity isn't in the index; this is only the
  // render gate + header stat, and any positive value opens the gate).
  const pFolders = extractFoldersFromPayload(payload);
  if (pFolders) {
    tableCount = pFolders.reduce((n, f) => n + (Number(f.file_count) || 0), 0);
    sensitiveCount = pFolders.filter((f) => (Number(f.finding_count) || 0) > 0).length;
  }

  // Table-index runs (PeopleSoft-80K): the persisted payload IS the index, so the header
  // counts come straight off its aggregate fields rather than from a `tables` array.
  const pIndex = extractIndexFromPayload(payload);
  if (pIndex) {
    tableCount = Number(pIndex.total_tables) || 0;
    sensitiveCount = Number(pIndex.sensitive_table_count) || 0;
  }

  const dbFromPayload = payload.database ?? payload.dbname ?? '';

  return {
    run_id: String(jobId),
    label: job.runLabel ?? String(jobId),
    // Raw label (no id fallback) so the results header can build a friendlier
    // "<source> — <date>" title for unlabeled runs instead of showing the job id.
    runLabel: job.runLabel ?? null,
    start_date: job.startedAt ? Date.parse(job.startedAt) : null,
    completed_at: job.finishedAt ? Date.parse(job.finishedAt) : null,
    status: mapJobStatusToUi(job.status) || job.status,
    engine: job.engine ?? '',
    connection: job.dataSourceName ?? '',
    dataSourceId: job.dataSourceId ?? null,
    database: dbFromPayload,
    table_count: tableCount,
    sensitive_table_count: sensitiveCount,
    // Relational tables the worker skipped pre-flight because they held zero rows. Present on
    // both the classic overview and the table-index payload under the same key; omitted (→ 0)
    // when nothing was skipped or for file/NoSQL runs.
    empty_tables_skipped_count: Number(payload.empty_tables_skipped_count) || 0,
    sampling_rules: job.samplingRuleName ?? '',
    workItemKind: job.workItemKind ?? null,
    workItemsCompleted: job.workItemsCompleted ?? null,
    workItemsTotal: job.workItemsTotal ?? null,
    // Why a run ended badly. Carried so the results view can CALL OUT that what it is
    // showing is partial: a discover that failed at 90% after twelve hours still has real
    // findings, and presenting them with no mark of incompleteness is the actual danger —
    // the user reads a partial sweep as full coverage.
    error_message: job.upstreamErrorBody || job.errorCode || null,
    tables,
    connectionDetails: { database: dbFromPayload, schema: payload.schema },
  };
};

/**
 * @param {string|number} jobId
 * @param {{ useCache?: boolean }} [opts] Pass `useCache: false` for bulk callers that fetch
 *   one overview per data source (Masking/Encryption source tables). Those pages don't need
 *   the payload to survive past the row build, and letting them populate the shared LRU would
 *   evict the payload the user is actively viewing on the results page (audit §3 UI).
 */
export const getOverview = async (jobId, opts = {}) => {
  const useCache = opts.useCache !== false;
  const key = cacheKey(jobId);
  let payload;
  const cached = useCache ? cacheGet(key) : undefined;
  if (cached && Date.now() - cached.at < 120000) {
    payload = cached.payload;
  } else {
    const res = await getJobResults(jobId);
    payload = res?.payload ?? {};
    if (useCache) cacheSet(key, { payload, at: Date.now() });
  }
  const tables = extractTablesFromPayload(payload);
  return {
    run_id: String(jobId),
    tables,
    scan_meta: payload.scan_meta || null,
    // Relational tables the worker skipped pre-flight because they held zero rows — they are NOT
    // in `tables`, so a caller reporting "N of M tables" is reporting M = tables PROCESSED and
    // needs this to account for the rest. 0 for file/NoSQL runs and when nothing was skipped.
    empty_tables_skipped_count: Number(payload.empty_tables_skipped_count) || 0,
    // Non-null only for Folder Search runs persisted in the folder-index shape.
    folders: extractFoldersFromPayload(payload),
    // Non-null only for table-index (PeopleSoft-80K) runs. The index payload is small
    // (that's the whole point), so this costs nothing extra beyond the getJobResults
    // call every getOverview caller already makes.
    tableIndex: extractIndexFromPayload(payload),
    // Whole-run statistics the worker computes once at results-assembly time (~4 KB).
    // Absent on runs that predate it, which is why every consumer must keep its fallback.
    runSummary: payload.run_summary || null,
  };
};

export const getTableResult = async (jobId, tableName) => {
  const overview = await getOverview(jobId);
  // Table-index runs: no per-table rows in the stored payload at all — go straight to
  // the one-table endpoint. No full overview/tables-page load is ever made for this path.
  if (overview.tableIndex) {
    let chunk = await getTableChunk(jobId, tableName).catch(() => null);
    let chunkTable = chunk?.table || (chunk && chunk.name != null ? chunk : null);
    // Schema-qualified key (`schema.table` — see overviewTableKey): the worker knows the
    // table by its bare name; retry split on the FIRST dot with the schema param. Only
    // after an exact miss, so relational tables whose names legitimately contain dots
    // keep resolving as before. Index-shaped runs are always relational (never files).
    if (!chunkTable && tableName.includes('.')) {
      const dot = tableName.indexOf('.');
      chunk = await getTableChunk(jobId, tableName.slice(dot + 1), tableName.slice(0, dot))
        .catch(() => null);
      chunkTable = chunk?.table || (chunk && chunk.name != null ? chunk : null);
    }
    return {
      run_id: String(jobId),
      table: chunkTable || { name: tableName, fields: [], rows_sampled: 0 },
    };
  }
  // Folder Search runs: the stored payload holds no per-file rows — pull the one
  // chunk that contains this file (direct-children grouping by parent folder).
  if (overview.folders) {
    const chunk = await getFolderChunk(jobId, parentFolderOfPath(tableName));
    const chunkTable = (chunk?.tables || []).find((t) => t.name === tableName);
    return {
      run_id: String(jobId),
      table: chunkTable || { name: tableName, fields: [], rows_sampled: 0 },
    };
  }
  // Exact-name match first (bare-name links from older views, file rows whose names
  // contain dots), then the schema-qualified key the scatter Y-axis links carry.
  const table = overview.tables.find((t) => t.name === tableName)
    || overview.tables.find((t) => overviewTableKey(t) === tableName);
  return {
    run_id: String(jobId),
    table: table || { name: tableName, fields: [], rows_sampled: 0 },
  };
};

// Page size for the export-time results/tables walk. Server caps detail=full at 200/page
// (see getResultsTablesPage doc); use the max to minimize round trips.
const EXPORT_PAGE_SIZE = 200;

/**
 * Full results payload for export. Classic runs: the stored payload as-is. Folder
 * Search runs (index-shaped payload): reassemble the monolith by unioning every
 * indexed folder's chunk — chunks tile the run, so the union equals what the old
 * monolithic payload contained. Table-index runs (PeopleSoft-80K): page through
 * results/tables?detail=full until exhausted and reassemble the same classic
 * `{tables: [...]}` shape. Export is user-initiated and rare; this is the one place
 * the UI deliberately pulls the whole run.
 */
export const getResultsPayloadForExport = async (jobId) => {
  const res = await getJobResults(jobId);
  const payload = res?.payload ?? {};

  const tableIndex = extractIndexFromPayload(payload);
  if (tableIndex) {
    const tables = [];
    const totalTables = Number(tableIndex.total_tables) || 0;
    // Safety cap on round trips in case the page response never signals "last page" the
    // way we expect below — export is user-initiated, but must still terminate.
    const maxPages = Math.max(1, Math.ceil(totalTables / EXPORT_PAGE_SIZE) + 2);
    for (let page = 0; page < maxPages; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const pageResp = await getResultsTablesPage(jobId, {
        page,
        size: EXPORT_PAGE_SIZE,
        detail: 'full',
      });
      const items = (pageResp && pageResp.tables) || [];
      tables.push(...items);
      const totalPages = pageResp && (pageResp.total_pages ?? pageResp.totalPages);
      const isLastPage = totalPages != null ? page + 1 >= totalPages : items.length < EXPORT_PAGE_SIZE;
      if (isLastPage) break;
    }
    return { run_id: tableIndex.run_id || payload.run_id, tables, scan_meta: tableIndex.scan_meta || null };
  }

  const folders = extractFoldersFromPayload(payload);
  if (!folders) return payload;
  const chunks = await Promise.all(
    folders.map((f) => getFolderChunk(jobId, f.folder_path).catch(() => null))
  );
  const tables = [];
  chunks.filter(Boolean).forEach((c) => tables.push(...(c.tables || [])));
  return { run_id: payload.run_id, tables, scan_meta: payload.scan_meta || null };
};

export const deleteRun = async (jobId) => {
  clearDiscoverResultsCache(jobId);
  await API.del(EP, `${JOBS}/${encodeURIComponent(jobId)}`);
};

export const cancelDiscoverJob = async (jobId) => {
  if (jobId == null || jobId === '') {
    throw new Error('Missing job id for cancel request.');
  }
  await API.post(EP, `/api/v1/discover/jobs/${encodeURIComponent(jobId)}/cancel`);
};

/**
 * @deprecated Was a misnamed alias that POSTed DISCOVER job ids to the MASKING cancel endpoint.
 * The platform-api's MaskingJobController filters by jobType=MASKING and falls back to looking up
 * by externalJobId, so the ids almost always 404'd ("Job not found"). When they accidentally
 * matched (e.g. a masking job with a numeric externalJobId), the wrong job got cancelled. Use
 * {@link cancelDiscoverJob} for discover, or add a `cancelMaskingJobById` if/when a real masking
 * cancel UI is built.
 */
export const cancelMaskingJob = cancelDiscoverJob;

/**
 * Build getExportData-shaped object for MongoDB QE / Protect dialog from discover results.
 * @param {object} payload - worker payload
 * @param {object} opts - { database, schema, connection, searchid, sampleRulesValue? }
 */
export function buildProtectExportFromPayload(payload, opts = {}) {
  const tables = extractTablesFromPayload(payload);
  const tablesValue = tables.map((t) => ({
    name: t.name,
    columns: (t.fields || []).map((f) => ({
      column_name: f.name,
      data_type: f.data_type || 'string',
      found_entities: f.findings || {},
    })),
  }));
  const database = opts.database || payload?.database || payload?.dbname || '';
  const schema = opts.schema || payload?.schema || database || 'public';
  return {
    searchid: opts.searchid,
    database,
    schema,
    tablesValue,
    connection: opts.connection,
    engine: opts.engine,
    sampleRulesValue: opts.sampleRulesValue,
  };
}

/**
 * Paginated discover jobs → grouped rows for Discovery Reports table (Material-Tree parent/child).
 * @param {Array<object>} jobs - OrchestrationJobResponseDTO[] (DISCOVER only)
 * @param {number} pageIndex - zero-based UI page (for synthetic parent ids)
 */
export function transformDiscoverJobsToReportRows(jobs = [], pageIndex = 0) {
  const discover = jobs.filter((j) => j && j.jobType === 'DISCOVER');
  const byDataSource = new Map();

  // Group by dataSourceId (stable). Falls back to name for jobs that pre-date the field
  // being populated; rename-safe in the common case.
  discover.forEach((job) => {
    const key = job.dataSourceId != null ? `id:${job.dataSourceId}` : `name:${job.dataSourceName || ''}`;
    if (!byDataSource.has(key)) {
      byDataSource.set(key, []);
    }
    byDataSource.get(key).push(job);
  });

  const result = [];
  const groupKeys = Array.from(byDataSource.keys());
  groupKeys.sort((a, b) => {
    const maxA = Math.max(...byDataSource.get(a).map((j) => ts(j)));
    const maxB = Math.max(...byDataSource.get(b).map((j) => ts(j)));
    return maxB - maxA;
  });

  function ts(j) {
    const t = j.finishedAt || j.startedAt;
    return t ? Date.parse(t) : 0;
  }

  groupKeys.forEach((groupKey) => {
    const items = byDataSource.get(groupKey).slice().sort((a, b) => ts(b) - ts(a));
    const mostRecent = items[0];
    const connectionName = mostRecent.dataSourceName || '';
    const dataSourceId = mostRecent.dataSourceId ?? null;
    const parentKey = dataSourceId != null ? `id-${dataSourceId}` : `name-${connectionName}`;
    const connectionParentId = `connection_${parentKey}_p${pageIndex}`;

    result.push({
      searchid: connectionParentId,
      connection: connectionName,
      dataSourceId,
      type: mostRecent.engine,
      date: ts(mostRecent),
      displayId: mostRecent.runLabel || String(mostRecent.id),
      sampleRules: mostRecent.samplingRuleName || '-',
      mostRecentSearchId: String(mostRecent.id),
      isConnectionParent: true,
    });

    for (let i = 1; i < items.length; i += 1) {
      const job = items[i];
      result.push({
        searchid: String(job.id),
        connection: job.dataSourceName || connectionName,
        dataSourceId: job.dataSourceId ?? dataSourceId,
        type: job.engine,
        date: ts(job),
        displayId: job.runLabel || String(job.id),
        sampleRules: job.samplingRuleName || '-',
        parent_connection: connectionParentId,
      });
    }
  });

  return result;
}

export async function listDiscoverJobsPage(page, size, sort = 'startedAt,desc', dataSourceId) {
  const params = { page, size, sort };
  if (dataSourceId != null) params.dataSourceId = dataSourceId;
  const pageDto = await API.get(EP, JOBS, { queryStringParameters: params });
  const content = (pageDto && pageDto.content) || [];
  const discoverJobs = content.filter((j) => j && j.jobType === 'DISCOVER');
  return { pageDto, discoverJobs };
}

// INTERRUPTED counts as failed here: it's a worker-restart casualty the user retries by
// starting a new run, so pre-run cleanup should sweep it like any other failed run.
const FAILED_JOB_STATUSES = new Set(['FAILED', 'ERROR', 'TIMEOUT', 'INTERRUPTED']);

/**
 * Delete all failed discover jobs for one datasource before starting a new run.
 * @param {number} dataSourceId
 * @returns {Promise<number>} number of deleted runs
 */
export async function deleteFailedRunsForDataSource(dataSourceId) {
  if (dataSourceId == null) return 0;

  let page = 0;
  let totalPages = 1;
  const failedJobIds = [];

  while (page < totalPages) {
    const { pageDto, discoverJobs } = await listDiscoverJobsPage(page, 200, 'startedAt,desc', dataSourceId);
    totalPages = pageDto && pageDto.totalPages != null ? Number(pageDto.totalPages) : 1;
    discoverJobs.forEach((job) => {
      if (job && FAILED_JOB_STATUSES.has(job.status) && job.id != null) {
        failedJobIds.push(job.id);
      }
    });
    page += 1;
  }

  if (failedJobIds.length === 0) return 0;

  await Promise.all(failedJobIds.map((jobId) => deleteRun(jobId)));
  return failedJobIds.length;
}

// --- Thin derivation helpers (unchanged consumers) ---

export const sumFindingsByType = (tables = []) => {
  const totals = {};
  tables.forEach((table) => {
    (table.fields || []).forEach((field) => {
      const findings = field.findings || {};
      Object.entries(findings).forEach(([type, count]) => {
        totals[type] = (totals[type] || 0) + count;
      });
    });
  });
  return totals;
};

export const groupTablesByEntityType = (tables = []) => {
  const byEntity = {};
  tables.forEach((table) => {
    const tableName = table.name;
    const rowsSampled = table.rows_sampled || 0;
    (table.fields || []).forEach((field) => {
      const findings = field.findings || {};
      Object.entries(findings).forEach(([type, count]) => {
        if (!byEntity[type]) byEntity[type] = [];
        const existing = byEntity[type].find((t) => t.tableName === tableName);
        if (existing) {
          existing.totalCount += count;
        } else {
          byEntity[type].push({ tableName, totalCount: count, rows_sampled: rowsSampled });
        }
      });
    });
  });
  return byEntity;
};

export const getTableSampledRowCountsMap = (tables = []) => {
  const map = {};
  tables.forEach((t) => {
    if (t.name != null) map[t.name] = t.rows_sampled ?? 0;
  });
  return map;
};

const LOW_CONFIDENCE_THRESHOLD = 5;

/** Same rule as table panel / scatter: argmax count per field, ≤10% of sampled rows = low incidence. */
export const getDominantEntityIncidence = (entities, sampledRowCount) => {
  const map = entities || {};
  const keys = Object.keys(map);
  if (!keys.length) {
    return {
      dominantEntity: null,
      dominantCount: 0,
      dominantPercent: 0,
      isLowIncidence: true,
      isMetadataOnly: false
    };
  }
  let dominantEntity = keys[0];
  let dominantCount = Number(map[dominantEntity]) || 0;
  for (let i = 1; i < keys.length; i++) {
    const k = keys[i];
    const c = Number(map[k]) || 0;
    if (c > dominantCount || (c === dominantCount && k.localeCompare(dominantEntity) < 0)) {
      dominantEntity = k;
      dominantCount = c;
    }
  }
  // Metadata-only discovery: the service identified the entity from the column
  // name alone (no sampled instances, or no rows were sampled at all). Force it
  // into the main feed as a single finding so the label shows up at the top of
  // the field.
  if (!(dominantCount > 0) || !sampledRowCount) {
    return {
      dominantEntity,
      dominantCount: 1,
      dominantPercent: 0,
      isLowIncidence: false,
      isMetadataOnly: true
    };
  }
  const dominantPercent = Math.floor((dominantCount / sampledRowCount) * 100);
  const isLowIncidence = dominantPercent <= 2;
  return { dominantEntity, dominantCount, dominantPercent, isLowIncidence, isMetadataOnly: false };
};

/**
 * Pie + entity list counts: each field contributes only its dominant entity’s count
 * (matches Sensitive Data Distribution and table panel classification at 10%).
 */
export const buildOverviewForPieVis = (tables = []) => {
  const main = {};
  const low = {};
  tables.forEach((table) => {
    const rowsSampled = table.rows_sampled || 1;
    (table.fields || []).forEach((field) => {
      const findings = field.findings || {};
      if (Object.keys(findings).length === 0) return;
      const { dominantEntity, isLowIncidence } = getDominantEntityIncidence(findings, rowsSampled);
      if (!dominantEntity) return;
      // Count fields (columns / JSON paths / XML elements) where the entity was
      // found, NOT the sum of sampled instance counts. Instance sums grow and
      // shrink with the sample rate, which misleads customers; the field count
      // is stable and answers the real question — how many places masking would
      // need to be configured. One increment per field per dominant entity.
      if (isLowIncidence) {
        low[dominantEntity] = (low[dominantEntity] || 0) + 1;
      } else {
        main[dominantEntity] = (main[dominantEntity] || 0) + 1;
      }
    });
  });
  if (Object.keys(low).length > 0) {
    return { ...main, LOW_INCIDENCE_FINDINGS: low };
  }
  return main;
};

export const buildTableSampledRowCountsMapForEntityTables = (tables = []) => {
  const map = {};
  tables.forEach((t) => {
    // Keyed by overviewTableKey — the scatter tooltip looks rows up by their (possibly
    // schema-qualified) Y-axis key. File rows carry no schema, so their keys are unchanged.
    const key = overviewTableKey(t);
    if (key != null) map[key] = { table_sampled_row_qty: t.rows_sampled ?? 0 };
  });
  return map;
};

export const buildEntityTablesFromOverview = (tables = []) => {
  const result = { TOTAL: {}, LOW_INCIDENCE_FINDINGS: { TOTAL: {} } };
  tables.forEach((table) => {
    const tableName = table.name;
    const rowsSampled = table.rows_sampled || 1;
    let tableTotal = 0;
    (table.fields || []).forEach((field) => {
      const findings = field.findings || {};
      Object.entries(findings).forEach(([entity, count]) => {
        tableTotal += count;
        const percent = Math.floor((count / rowsSampled) * 100);
        if (percent <= LOW_CONFIDENCE_THRESHOLD) {
          if (!result.LOW_INCIDENCE_FINDINGS[entity]) result.LOW_INCIDENCE_FINDINGS[entity] = {};
          result.LOW_INCIDENCE_FINDINGS[entity][tableName] = (result.LOW_INCIDENCE_FINDINGS[entity][tableName] || 0) + count;
          result.LOW_INCIDENCE_FINDINGS.TOTAL[tableName] = (result.LOW_INCIDENCE_FINDINGS.TOTAL[tableName] || 0) + count;
        } else {
          if (!result[entity]) result[entity] = {};
          result[entity][tableName] = (result[entity][tableName] || 0) + count;
        }
      });
    });
    result.TOTAL[tableName] = (result.TOTAL[tableName] || 0) + tableTotal;
  });
  return result;
};

export const buildTableColumnEntityMapFromOverview = (tables = []) => {
  const result = {};
  tables.forEach((table) => {
    const tableName = table.name;
    result[tableName] = [];
    (table.fields || []).forEach((field) => {
      const findings = [];
      Object.entries(field.findings || {}).forEach(([entity, count]) => {
        findings.push({ entity, count });
      });
      if (findings.length > 0) result[tableName].push({ col: field.name, findings });
    });
  });
  return result;
};

export const mapTableResultToFoundEntitiesShape = (response) => {
  const table = response?.table;
  if (!table) return { rows_count: 0 };

  const tableName = table.name;
  const result = { [tableName]: {}, rows_count: table.rows_sampled || 0 };
  if (table.meta) result._meta = table.meta;
  if (table.model_timeout_batches > 0) result._timeout_batches = table.model_timeout_batches;
  // t2-1: per-table processing failure + model-rejected (4xx) batch count, rendered as
  // banners in TablePanel. Underscore-prefixed like _timeout_batches so consumers that
  // iterate table keys never mistake them for column groups.
  if (table.model_error_batches > 0) result._model_error_batches = table.model_error_batches;
  if (table.error_message) result._error_message = table.error_message;
  // Sniffed per-row format (a DatasourceType name; present on Folder Search chunk rows and
  // sniff-divergent typed-run rows). TablePanel uses it to pick the structured view for
  // JSON/XML files.
  if (table.sampler_type) result._sampler_type = table.sampler_type;
  // Schema/owner of the table (omitted by the worker when empty — file/NoSQL/owner-less
  // runs). TablePanel renders it as a badge next to the source name.
  if (table.schema) result._schema = table.schema;
  (table.fields || []).forEach((field) => {
    result[tableName][field.name] = {
      entities: field.findings || {},
      ...(field.constraints && field.constraints.length > 0 && { constraints: field.constraints }),
    };
  });
  // Alias the fields map under the schema-qualified key too (same object, not a copy):
  // scatter links carry `schema.table` route params while older links carry the bare
  // name — consumers index this shape by their route param, so both must resolve.
  const qualifiedKey = overviewTableKey(table);
  if (qualifiedKey && qualifiedKey !== tableName) result[qualifiedKey] = result[tableName];
  return result;
};

export const mapHistoryRunsToSearchStatuses = (runs = []) => {
  const statusMap = {
    QUEUED: 'QUEUED',
    RUNNING: 'RUNNING',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
    COMPLETED_WITH_ERRORS: 'COMPLETED_WITH_ERRORS',
  };
  return runs.map((run) => {
    const glue_status = statusMap[run.status] || run.status;
    const jobIdStr = run.run_id != null ? String(run.run_id) : run.jobId != null ? String(run.jobId) : undefined;
    const row = {
      searchid: jobIdStr,
      run_id: jobIdStr,
      jobId: jobIdStr,
      creationTime: run.start_date ?? run.completed_at ?? Date.now(),
      glue_status,
      state: glue_status,
      connection: run.connection,
      searchName: run.label || jobIdStr || run.run_id || run.jobId,
      sampleRules: run.sampling_rules || '-',
      type: run.engine,
      parent_searchid: run.parent_run_id || undefined,
      last_updated: Date.now(),
      completed_at: run.completed_at ?? null,
    };
    if (run.error_message != null) row.error_message = run.error_message;
    if (run.upstream_error != null) row.upstream_error = run.upstream_error;
    if (run.notice != null) row.notice = run.notice;
    if (run.total_rows_to_process != null) row.total_rows = run.total_rows_to_process;
    if (run.rows_processed != null) row.total_processed = run.rows_processed;
    if (run.workItemKind != null) row.workItemKind = run.workItemKind;
    if (run.workItemsCompleted != null) row.workItemsCompleted = run.workItemsCompleted;
    if (run.workItemsTotal != null) row.workItemsTotal = run.workItemsTotal;
    if (run.dataSourceId != null) row.dataSourceId = run.dataSourceId;
    return row;
  });
};

/**
 * Download the archive-skipped-files CSV for a generic folder scan run.
 * Returns a Blob (text/csv) or null when the run had no archive skips (204).
 * Throws on HTTP error.
 */
export const downloadSkippedArchiveFiles = async (runId) => {
  const base = (API.Endpoints[EP] && API.Endpoints[EP].endpoint) || '';
  const token = API.Endpoints[EP] && API.Endpoints[EP].jwtToken;
  const url = `${base}/api/v1/jobs/discover/file/${encodeURIComponent(runId)}/generic/skipped-files/download`;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(url, { headers });
  if (res.status === 204) return null;
  if (!res.ok) throw new Error(`Skipped files download failed: ${res.status}`);
  return res.blob();
};

/**
 * Fetch the archive-skipped-files list as JSON for a discover job.
 * Returns { entries: [{ filePath, skipReason, reasonText, extension }], legend: {} }
 * with an empty entries array when the job had no archive skips. Throws on HTTP error.
 *
 * Hits the job-id-keyed endpoint on the platform; the platform resolves the worker run
 * UUID internally — passing the worker UUID directly would not work.
 */
export const getSkippedArchiveFiles = async (jobId) => {
  return API.get(EP, `${JOBS}/${encodeURIComponent(jobId)}/skipped-files`);
};
